import { EditIcon } from '@chakra-ui/icons';
import {
    Box,
    HStack,
    VStack,
    Heading,
    Progress,
    useBoolean,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { MdDialpad } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody } from 'components/v4/Card';
import Text from 'components/v4/Text';
import Image from 'components/v4/Image';
import { MenuItem } from 'components/v4';
import DashboardCardEditButton from '../../../components/DashboardCardEditButton';
import CardDetails from '../../../components/CardDetails';
import styles from '../../../styles';
import { useServicesDashboard } from 'context/ServicesDashboardContext';
import { useNavigateLocation } from 'hooks/useNavigateLocation/useNavigateLocation';

import path from '@/routes/constants';
import walkthroughIds from 'pages/services/walkthroughIds';
import { OPERATOR_CONNECT } from '../../../constants';
import ServiceStatus from '../ServiceStatus';
import ManageNumbers from './ManageNumbers';

const TPMCard = ({ service, companyData, serviceWizard }) => {
    const isTrialExpired =
        companyData?.isTrial &&
        new Date(companyData?.trialEnd + 'Z') < new Date();

    const navigate = useNavigate();
    const { handleNavigateLocation: navigateLocation } = useNavigateLocation();

    const serviceStatus = useServicesDashboard();
    const cardStatus = serviceStatus[OPERATOR_CONNECT];

    const [hover, setHover] = useBoolean();
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    const { ufTpmEditServiceButton, ufTpmManageAccountButton } =
        walkthroughIds.dashboardServices;
    const tpmColorScheme = isTrialExpired ? 'red' : cardStatus?.color;
    const handleActions = {
        handleGrantAccess: serviceWizard?.microsoftAuth,
    };

    useEffect(() => {
        const alert = document.querySelector('.chakra-alert');

        if (!alert || !hover || !isTrialExpired) return;

        const classList = [
            'animate-pulse-slow',
            darkMode
                ? '!bg-[var(--chakra-colors-blue-900)]'
                : '!bg-[var(--chakra-colors-blue-200)]',
        ];
        alert.classList.add(...classList);

        return () => alert.classList.remove(...classList);
    }, [hover, isTrialExpired, darkMode]);

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                minHeight: '190px',
                minWidth: '250px',
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            <Progress
                value={100}
                sx={styles.verticalBar}
                colorScheme={tpmColorScheme}
                hasStripe={cardStatus?.animation}
                isAnimated={cardStatus?.animation}
            />
            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                }}>
                <VStack
                    alignItems="flex-start"
                    width="100%"
                    spacing={2}
                    marginBottom="15px">
                    <HStack
                        alignItems="flex-start"
                        width="100%"
                        overflow="hidden">
                        <Image
                            className="rounded-xl"
                            src={CardDetails(service?.serviceType)?.icon}
                            sx={{ maxWidth: '50px', flexShrink: 0 }}
                        />
                        <VStack alignItems="flex-start" width="100%" minW={0}>
                            <Heading as="h4" size="md">
                                {CardDetails(service?.serviceType)?.serviceName}
                            </Heading>
                            {/* Status */}
                            <Box
                                className="flex justify-between"
                                sx={{ paddingBottom: '20px', width: '100%' }}>
                                <ServiceStatus
                                    service={service}
                                    cardStatus={cardStatus}
                                    isTrialExpired={isTrialExpired}
                                    handleActions={handleActions}
                                />
                            </Box>

                            {/* Manage Numbers */}
                            <ManageNumbers
                                cardStatus={cardStatus}
                                isTrialExpired={isTrialExpired}
                                darkMode={darkMode}
                                numbersPath={path.NUMBERS}
                            />
                        </VStack>
                    </HStack>
                </VStack>
                <DashboardCardEditButton>
                    {requiredScope >= 40 && (
                        <MenuItem
                            icon={<EditIcon />}
                            onClick={() => {
                                navigate(`/services/tpm/${service.id}/edit`);
                            }}
                            data-walkthroughid={ufTpmEditServiceButton}>
                            <Text marginBottom="0">Edit Service</Text>
                        </MenuItem>
                    )}
                    <MenuItem
                        icon={<MdDialpad />}
                        onClick={() => {
                            navigateLocation('/numbers');
                        }}
                        onPointerEnter={setHover.on}
                        onPointerLeave={setHover.off}
                        isDisabled={isTrialExpired}
                        data-walkthroughid={ufTpmManageAccountButton}>
                        <Text marginBottom="0">Number Management</Text>
                    </MenuItem>
                </DashboardCardEditButton>
            </CardBody>
        </Card>
    );
};

export default TPMCard;
